import React, { FC, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { cn } from '@utils/cn';
import { RICH_TEXT_ALLOWED_TAGS, isProbablyHtml } from '@utils/richText';

interface RichTextViewProps {
  /**
   * Sanitized HTML, or a legacy plain-text description. The component detects which it is
   * — callers never have to.
   */
  value?: string | null;
  className?: string;
}

/**
 * Sanitize on the read path as well as the write path.
 *
 * The backend is the source of truth: a column transformer on `Task.description` sanitizes
 * everything on the way in. This second pass is deliberate redundancy, and it earns its keep
 * twice — it covers rows written before that transformer shipped, and it means a future drift
 * between the three repos' `isProbablyHtml` copies can never become an execution bug.
 *
 * `RETURN_DOM_FRAGMENT` plus an explicit fixup loop is used instead of `DOMPurify.addHook`,
 * because hooks are registered globally and would silently leak into any other sanitize call
 * added to this app later.
 */
const sanitizeToHtml = (html: string): string => {
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: RICH_TEXT_ALLOWED_TAGS,
    ALLOWED_ATTR: ['href', 'target', 'rel'],
    FORBID_ATTR: ['style', 'class', 'id'],
    RETURN_DOM_FRAGMENT: true,
  });

  fragment.querySelectorAll('a').forEach((anchor) => {
    anchor.setAttribute('target', '_blank');
    anchor.setAttribute('rel', 'noopener noreferrer nofollow');
  });

  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
};

/**
 * Renders a task description, whether it is rich HTML or a legacy plain-text row.
 *
 * There is deliberately no clamping prop: `line-clamp` relies on `display:-webkit-box`, which
 * does not work once the content has nested block children. Callers that need a truncated
 * preview should run the value through `htmlToPlainText()` and clamp that instead.
 */
export const RichTextView: FC<RichTextViewProps> = ({ value, className }) => {
  const html = useMemo(
    () => (value && isProbablyHtml(value) ? sanitizeToHtml(value) : null),
    [value],
  );

  if (!value) {
    return null;
  }

  // Legacy plain text: let React escape it and let CSS honour the newlines.
  if (html === null) {
    return <p className={cn('whitespace-pre-line break-words', className)}>{value}</p>;
  }

  return (
    <div
      className={cn('rich-text break-words', className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default RichTextView;
